<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import useVuelidate from "@vuelidate/core";
import { helpers, required, email as emailValidator, minLength, maxLength } from "@vuelidate/validators";
import { BekostigingsType } from "@/repositories/activiteit.repository";
import { Tooltip } from "floating-vue";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import { useProfileStore } from "@/stores/profile.store";
import { formatCurrency } from "@/helpers/currency.helper";
import { useValidationHelpers, scrollToFirstError, createLeeftijdRangeValidator } from "@/helpers/validation.helper";
import { DatePicker } from "v-calendar";
import BestelVolwasseneForm from "./BestelVolwasseneForm.vue";
import BestelKindForm from "./BestelKindForm.vue";
import BestelVraagInput from "./BestelVraagInput.vue";

const store = useSportactiviteitBestelStore();
const profileStore = useProfileStore();

const emits = defineEmits<{
    (e: "volgende"): void;
    (e: "terug"): void;
}>();

const showBirthdateInfo = ref(false);
const showAdditionalFields = ref(false);
const showKortingscodeInput = ref(false);

const { account, boeking } = storeToRefs(store);

const volwassenen = computed(() => boeking.value.Volwassenen);
const kinderen = computed(() => boeking.value.Kinderen);

onMounted(async () => {
    await Promise.all([
        profileStore.ensureProfile(),
        profileStore.ensureKinderen(),
    ]);
    if (profileStore.isLoggedIn && profileStore.profile && !account.value.Voornaam) {
        account.value.Voornaam = profileStore.profile.Voornaam;
        account.value.Achternaam = profileStore.profile.Achternaam;
        account.value.Emailadres = profileStore.profile.Emailadres;
        account.value.Telefoonnummer = profileStore.profile.Telefoonnummer || "";
    }
});

// Profile children available for autofill (exclude children already booked by their Id)
const availableProfileKinderenFor = (index: number) => {
    const bookedIds = new Set(
        boeking.value.Kinderen
            .filter((_, i) => i !== index)
            .map((k) => k.Id)
            .filter((id): id is number => id !== undefined),
    );
    return profileStore.profileKinderen.filter((k) => k.Id === undefined || !bookedIds.has(k.Id));
};

const isLoggedIn = computed(() => profileStore.isLoggedIn);
const isGast = computed(() => store.guestAccount);
const isAbonnement = computed(() => store.activiteit?.Bekostigingstype === BekostigingsType.ABONNEMENT);
const isBetaling = computed(
    () => (
        store.activiteit?.Bekostigingstype === BekostigingsType.BETALING
        || store.activiteit?.Bekostigingstype === BekostigingsType.ABONNEMENT
    ) && store.paymentMethods.length > 0,
);

const gegevensTitle = computed(() => (isLoggedIn.value ? "Jouw gegevens" : "Contactgegevens hoofdboeker"));

const toonJezelf = computed(() => {
    if (isGast.value) return true;
    const leeftijd = profileStore.profile?.Leeftijd;
    if (leeftijd == null) return true;
    const min = store.activiteit?.MinLeeftijd ?? 0;
    const max = store.activiteit?.MaxLeeftijd ?? 999;
    return leeftijd >= min && leeftijd <= max;
});

const toonVolwassenen = computed(() => (store.activiteit?.MaxLeeftijd ?? 0) >= 18);
const toonKinderen = computed(() => (store.activiteit?.MinLeeftijd ?? 18) < 18 && !isGast.value);
const requiresExtraKostenAkkoord = computed(() => Boolean(store.activiteit?.ExtraKosten));
const isExtraKostenAkkoordValid = computed(() => !requiresExtraKostenAkkoord.value || boeking.value.ExtraKostenAkkoord);
const extraKostenAkkoordTouched = ref(false);

const algemeneVoorwaardenLabel = computed(() => store.algemeneVoorwaardenLabel || "Ik ga akkoord met de algemene voorwaarden");
const isAkkoordVoorwaardenValid = computed(() => boeking.value.AkkoordVoorwaarden);
const akkoordVoorwaardenTouched = ref(false);

const today = new Date();

const telefoonnummerPatternValidator = helpers.withMessage(
    "Dit is geen geldig telefoonnummer",
    (val: string) => !val || /^[0-9\s+\-()]{10,15}$/.test(val),
);

// Definieer de regex als custom validator
const dutchPostalCode = helpers.regex(/^[1-9][0-9]{3}\s?[A-Za-z]{2}$/)

const accountRules = computed(() => ({
    Voornaam: !isLoggedIn.value
        ? { required: helpers.withMessage("Dit veld is verplicht", required) }
        : {},
    Achternaam: !isLoggedIn.value
        ? { required: helpers.withMessage("Dit veld is verplicht", required) }
        : {},
    Emailadres: !isLoggedIn.value
        ? {
            required: helpers.withMessage("Dit veld is verplicht", required),
            email: helpers.withMessage("Dit is geen geldig e-mailadres", emailValidator),
        }
        : {},
    EmailadresConfirm: isGast.value
        ? {
            required: helpers.withMessage("Dit veld is verplicht", required),
            matchEmail: helpers.withMessage("E-mailadressen komen niet overeen.", (val: string) => val === account.value.Emailadres),
        }
        : {},
    Geboortedatum: isGast.value
        ? {
            required: helpers.withMessage("Dit veld is verplicht", (val: Date | null) => val !== null),
            leeftijdRange: createLeeftijdRangeValidator(
                () => store.activiteit?.MinLeeftijd ?? 0,
                () => store.activiteit?.MaxLeeftijd ?? 999,
            ),
        }
        : {},
    Postcode: isGast.value
        ? {
            required: helpers.withMessage("Dit veld is verplicht", required),
            dutchPostalCode: helpers.withMessage("Dit is geen geldige postcode", dutchPostalCode)
        }
        : {},
    Telefoonnummer: {
        required: helpers.withMessage("Dit veld is verplicht", required),
        minLength: helpers.withMessage("Dit is geen geldige invoer. Vul meer tekens in.", minLength(10)),
        maxLength: helpers.withMessage("De invoer is te lang", maxLength(15)),
        pattern: telefoonnummerPatternValidator,
    },
    Noodnummer: {
        pattern: telefoonnummerPatternValidator,
        notSame: helpers.withMessage(
            "Je mobiele nummer en noodnummer kunnen niet hetzelfde zijn.",
            (val: string) => !val || val !== account.value.Telefoonnummer,
        ),
    },
}));

const paymentProviderRules = computed(() => ({
    PaymentProvider: isBetaling.value
        ? { required: helpers.withMessage("Selecteer een betaalmethode", required) }
        : {},
}));

const allRules = computed(() => ({
    account: accountRules.value,
    boeking: paymentProviderRules.value,
}));

const state = { account, boeking };
const v$ = useVuelidate(allRules, state);
const { getFieldErrors } = useValidationHelpers(v$);

const validate = async () => v$.value.$validate();
defineExpose({ validate });

const handleVolgende = async () => {
    extraKostenAkkoordTouched.value = true;
    akkoordVoorwaardenTouched.value = true;

    if (!store.isInschrijvingPeriodeActief) {
        return;
    }

    if (!isExtraKostenAkkoordValid.value) {
        const extraKostenAkkoordElement = document.getElementById("extraKostenAkkoord");
        extraKostenAkkoordElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        extraKostenAkkoordElement?.focus();
        return;
    }

    if (!isAkkoordVoorwaardenValid.value) {
        const akkoordVoorwaardenElement = document.getElementById("akkoordVoorwaarden");
        akkoordVoorwaardenElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        akkoordVoorwaardenElement?.focus();
        return;
    }

    const valid = await validate();
    if (valid) {
        emits("volgende");
    }
    else {
        scrollToFirstError();
    }
};
</script>

<template>
    <div class="gegevens-form">

        <!-- Hoofdboeker gegevens -->
        <div class="row">
            <div class="col-sm-12">
                <h1 class="payment-title h4 mb-2">{{ gegevensTitle }}</h1>
            </div>

            <!-- Voornaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.account.Voornaam.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="text" autocomplete="given-name" class="form-label-group-input" id="jezelf-Voornaam"
                        name="jezelf-Voornaam" v-model="account.Voornaam" :readonly="isLoggedIn" placeholder="Voornaam"
                        @blur="v$.account.Voornaam.$touch()" />
                    <label for="jezelf-Voornaam">Voornaam</label>
                    <div v-for="err in getFieldErrors('account.Voornaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Achternaam -->
            <div class="col-sm-6" :class="{ 'has-error': v$.account.Achternaam.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="text" autocomplete="family-name" class="form-label-group-input" id="jezelf-Achternaam"
                        name="jezelf-Achternaam" v-model="account.Achternaam" :readonly="isLoggedIn"
                        placeholder="Achternaam" @blur="v$.account.Achternaam.$touch()" />
                    <label for="jezelf-Achternaam">Achternaam</label>
                    <div v-for="err in getFieldErrors('account.Achternaam')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Emailadres -->
            <div class="col-sm-12" :class="{ 'has-error': v$.account.Emailadres.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="email" autocomplete="email" class="form-label-group-input" id="jezelf-Emailadres"
                        name="jezelf-Emailadres" v-model="account.Emailadres" :readonly="isLoggedIn"
                        placeholder="naam@domein.nl" @blur="v$.account.Emailadres.$touch()" />
                    <label for="jezelf-Emailadres">E-mailadres</label>
                    <div v-for="err in getFieldErrors('account.Emailadres')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Emailadres bevestigen (gast) -->
            <div v-if="isGast" class="col-sm-12" :class="{ 'has-error': v$.account.EmailadresConfirm.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="email" autocomplete="email" class="form-label-group-input"
                        id="jezelf-Emailadres-confirm" name="jezelf-Emailadres-confirm"
                        v-model="account.EmailadresConfirm" placeholder="Bevestig e-mailadres"
                        @blur="v$.account.EmailadresConfirm.$touch()" />
                    <label for="jezelf-Emailadres-confirm">Bevestig e-mailadres</label>
                    <div v-for="err in getFieldErrors('account.EmailadresConfirm')" :key="err" class="ic-error"
                        role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Geboortedatum (gast) -->
            <div v-if="isGast" class="col-sm-12" :class="{ 'has-error': v$.account.Geboortedatum.$error }">
                <div class="form-label-group mb-2 mb-md-3 date-field">
                    <DatePicker v-model="account.Geboortedatum" :popover="{ visibility: 'click' }" locale="nl"
                        color="platform-theme" :max-date="today"
                        @update:model-value="v$.account.Geboortedatum.$touch()">
                        <template v-slot="{ inputValue, inputEvents }">
                            <input id="jezelf-Geboortedatum" class="form-label-group-input" :value="inputValue"
                                v-on="inputEvents" placeholder="Geboortedatum" autocomplete="bday" />
                        </template>
                        <template #header-prev-button>
                            <span class="sr-only">Vorige maand</span>
                            <span class="icon-chevron--left" aria-hidden="true"></span>
                        </template>
                        <template #header-next-button>
                            <span class="sr-only">Volgende maand</span>
                            <span class="icon-chevron--right" aria-hidden="true"></span>
                        </template>
                    </DatePicker>
                    <label for="jezelf-Geboortedatum">Geboortedatum</label>
                    <div v-for="err in getFieldErrors('account.Geboortedatum')" :key="err" class="ic-error"
                        role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                    <button type="button" class="date-info-btn" @click="showBirthdateInfo = true">
                        <span class="icon icon-info"></span>
                    </button>
                </div>
            </div>

            <!-- Geboortedatum info popup -->
            <div v-if="showBirthdateInfo" class="col-sm-12">
                <div class="info-popup">
                    <div class="info-popup-content">
                        <button class="info-popup-close-button" type="button" @click="showBirthdateInfo = false">
                            <span class="icon icon-close"></span>
                        </button>
                        <p class="info-popup-title">Waarom vragen we naar je geboortedatum?</p>
                        <p class="info-popup-discription">We hebben je geboortedatum nodig om je identiteit te
                            verifiëren en je
                            persoonlijke gegevens te beschermen.</p>
                    </div>
                </div>
            </div>

            <!-- Postcode (gast) -->
            <div v-if="isGast" class="col-sm-12" :class="{ 'has-error': v$.account.Postcode.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="text" autocomplete="postal-code" class="form-label-group-input" id="jezelf-Postcode"
                        name="jezelf-Postcode" v-model="account.Postcode" placeholder="Postcode"
                        @blur="v$.account.Postcode.$touch()" />
                    <label for="jezelf-Postcode">Postcode</label>
                    <div v-for="err in getFieldErrors('account.Postcode')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>

            <!-- Telefoonnummer -->
            <div class="col-sm-12" :class="{ 'has-error': v$.account.Telefoonnummer.$error }">
                <div class="form-label-group mb-2 mb-md-3">
                    <input type="tel" autocomplete="tel" class="form-label-group-input" id="telefoonnummer"
                        name="telefoonnummer" v-model="account.Telefoonnummer" placeholder="Jouw mobiele nummer"
                        @blur="v$.account.Telefoonnummer.$touch()" />
                    <label for="telefoonnummer">Telefoonnummer hoofdboeker</label>
                    <div v-for="err in getFieldErrors('account.Telefoonnummer')" :key="err" class="ic-error"
                        role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
            </div>
        </div>

        <hr class="my-1 my-md-0" />

        <!-- Deelnemers sectie (alleen voor niet-groepsactiviteiten) -->
        <template v-if="!store.activiteit?.Groepsactiviteit && !isAbonnement">
            <h2 class="payment-title h4 mb-1 mt-2">Deelnemers</h2>

            <!-- Jezelf checkbox -->
            <div v-if="toonJezelf" class="mb-3">
                <div class="row">
                    <div class="col-sm-12">
                        <div class="custom-control custom-checkbox">
                            <input class="custom-control-input" type="checkbox" id="jezelf-checkbox"
                                :checked="boeking.Jezelf.Include"
                                @change="store.setJezelfInclude(!boeking.Jezelf.Include)" />
                            <label class="custom-control-label" for="jezelf-checkbox">
                                Ik boek voor mezelf
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Individuele vragen voor jezelf -->
                <div v-if="boeking.Jezelf.Include && boeking.Jezelf.Vragen.length" class="mt-2">
                    <BestelVraagInput v-for="vraagAntwoord in boeking.Jezelf.Vragen" :key="vraagAntwoord.Id"
                        :vraag-antwoord="vraagAntwoord" prefix="jezelf" />
                </div>

                <hr class="my-2 my-md-3" />
            </div>

            <!-- Volwassenen -->
            <div v-if="toonVolwassenen">
                <BestelVolwasseneForm v-for="(item, idx) in volwassenen" :key="idx" :item="item" :index="idx"
                    @remove="store.removeVolwassene(idx)" />
                <a href="#" class="add-person-link" @click.prevent="store.addVolwassene()">
                    <span class="icon-plus" aria-hidden="true"></span>
                    Vriend(in) toevoegen
                </a>
            </div>

            <!-- Kinderen -->
            <div v-if="toonKinderen" class="mt-3">
                <BestelKindForm v-for="(item, idx) in kinderen" :key="idx" :item="item" :index="idx"
                    :available-profile-kinderen="availableProfileKinderenFor(idx)" @remove="store.removeKind(idx)" />
                <a href="#" class="add-person-link" @click.prevent="store.addKind()">
                    <span class="icon-plus" aria-hidden="true"></span>
                    Kind toevoegen
                </a>
            </div>
        </template>

        <!-- Betaalmethode -->
        <div v-if="isBetaling && store.showPaymentMethod" class="my-4">
            <h2 class="payment-title h4 mb-2">Betaalwijze</h2>
            <div v-for="method in store.paymentMethods" :key="method.Id" class="payment-method-box">
                <div class="card-body p-0">
                    <div class="payment-method-box">
                        <div class="custom-control custom-radio">
                            <input class="custom-control-input" type="radio" :id="`PaymentProvider-${method.Id}`"
                                name="PaymentProvider" :value="method.Id" v-model="boeking.PaymentProvider"
                                @change="v$.boeking.PaymentProvider.$touch()" />
                            <label class="custom-control-label" :for="`PaymentProvider-${method.Id}`">
                                <img v-if="method.ImageUrl" class="img-fluid d-inline-block mr-2" height="24"
                                    :src="method.ImageUrl" alt="" />
                                {{ method.Name }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="v$.boeking.PaymentProvider.$dirty" class="mt-1">
                <small v-for="error in v$.boeking.PaymentProvider.$errors" :key="error.$uid"
                    class="text-danger d-block">
                    {{ error.$message }}
                </small>
            </div>
        </div>

        <!-- Golfstart fouten -->
        <div v-if="!store.isGolfstartAllowed && !store.isProfileAllowed" class="alert alert-danger mt-3" role="alert">
            <small>Het is alleen mogelijk om de cursus 'Van baanpermissie naar Handicap 54' te volgen indien de cursus
                'Golfstart naar baanpermissie' met goed resultaat is afgerond. Controleer of alle personen in uw groep
                de cursus
                'Golfstart naar baanpermissie' hebben afgerond en of de opgegeven e-mailadressen dezelfde zijn als
                waarmee deze
                eerste cursus destijds geboekt is.</small>
        </div>
        <div v-if="!store.isGolfstartAllowed && !store.isFriendsAllowed && store.isProfileAllowed"
            class="alert alert-danger mt-3" role="alert">
            <small>Het is alleen mogelijk om de cursus 'Van baanpermissie naar Handicap 54' te volgen indien de cursus
                'Golfstart naar baanpermissie' met goed resultaat is afgerond. Controleer of alle personen in uw groep
                de cursus
                'Golfstart naar baanpermissie' hebben afgerond en of de opgegeven e-mailadressen dezelfde zijn als
                waarmee deze
                eerste cursus destijds geboekt is.</small>
        </div>

        <!-- Groepsvragen (VraagBedoeldVoor = VoorDeGroep) -->
        <div v-if="store.groepsVragen.length" class="mt-3">
            <h2 class="payment-title h4 mb-2">Vragen</h2>
            <BestelVraagInput v-for="vraagAntwoord in boeking.Vragen" :key="vraagAntwoord.Id"
                :vraag-antwoord="vraagAntwoord" prefix="groep" />
        </div>

        <!-- Kortingscode -->
        <div v-if="store.showKortingscodeOptie && !store.guestAccount && !isAbonnement"
            class="mb-5 mb-md-4 discount-box">
            <p class="discount-box-code-text">
                Ik heb een kortingscode
                <Tooltip :triggers="['click', 'hover']" :theme="'payment-info'">
                    <span class="icon icon-info" aria-hidden="true"></span>
                    <template #popper>
                        Het kortingsbedrag gaat af van het totale bedrag. Dit zijn de kosten voor deelname aan de
                        activiteit + de servicekosten.
                        Als door de korting het totaal bedrag lager dan &euro; 0,- is, krijg je geen geld terug.
                    </template>
                </Tooltip>
            </p>
            <button v-if="!showKortingscodeInput" type="button" class="discount-box-button"
                @click="showKortingscodeInput = true">
                <span class="icon-plus mr-1" aria-hidden="true"></span>
                Kortingscode invullen
            </button>
            <div v-if="showKortingscodeInput" class="discount-control-box">
                <!-- Neutral state -->
                <div class="form-label-group"
                    v-if="!store.validActiecode && !store.invalidActiecode && !store.lockedOut">
                    <input type="text" class="form-label-group-input" placeholder="Kortingscode"
                        aria-label="Kortingscode" v-model="store.kortingscode" />
                    <label>Kortingscode</label>
                    <div class="discount-control-container">
                        <button type="button" class="btn btn-primary"
                            :disabled="store.isValidatingActiecode || !store.kortingscode"
                            @click="store.validateActiecode()">
                            Activeren
                        </button>
                        <button type="button" class="btn btn-link font-weight-bold"
                            @click="showKortingscodeInput = false">
                            Annuleren
                        </button>
                    </div>
                </div>

                <!-- Success state -->
                <div class="discount-success" v-if="store.validActiecode">
                    <div class="discount-container success">
                        <div class="discount-icon-box success">
                            <span class="icon icon-succes" aria-hidden="true"></span>
                        </div>
                        <div class="discount-text-box">
                            <p class="discount-text-box-text">
                                Jouw code <span class="discount-code-text">{{ store.kortingscode }}</span> is
                                toegevoegd<span v-if="store.alleKosten?.Korting"> en heeft een waarde van {{
                                    formatCurrency(store.alleKosten.Korting) }}</span>.
                            </p>
                            <p v-if="store.validActiecode.Meta" class="discount-text-box-text">
                                <span class="font-weight-bold">{{ store.validActiecode.Meta }}</span> zit in je
                                bestelling
                            </p>
                            <button type="button" class="discount-agian-button"
                                @click="store.resetActiecode(); showKortingscodeInput = false">
                                Verwijder
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Error state -->
                <div class="discount-failed" v-if="store.invalidActiecode || store.lockedOut">
                    <div class="discount-container failed">
                        <div class="discount-icon-box failed">
                            <span class="icon icon-close" aria-hidden="true"></span>
                        </div>
                        <div class="discount-text-box">
                            <p v-if="store.invalidActiecode" class="discount-text-box-text">
                                <span class="icon-danger" aria-hidden="true"></span>
                                Jouw code <span class="discount-code-text">{{ store.kortingscode }}</span> is ongeldig.
                                <br />
                                Zou de ingevulde kortingscode naar jouw idee moeten werken en heb je hierover een vraag,
                                neem
                                dan contact met ons op.
                            </p>
                            <p v-if="store.lockedOut" class="discount-text-box-text">
                                <span class="icon-danger" aria-hidden="true"></span>
                                Kortingscode invullen geblokkeerd voor <span class="discount-code-text">24 uur</span>.
                                <br />
                                Je hebt te vaak een foutieve of eerder gebruikte kortingscode ingevuld. Mocht je vragen
                                hierover
                                hebben, neem dan contact met ons op.
                            </p>
                            <button type="button" class="discount-agian-button" @click="store.resetActiecode()">
                                Probeer opnieuw
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Overige gegevens -->
        <div class="mt-4 mb-2">
            <h2 class="payment-small-title mb-1">
                Overige gegevens
                <span class="ml-1 optional-text">optioneel</span>
                <Tooltip :triggers="['click', 'hover']" :theme="'payment-info'">
                    <span class="icon icon-info" aria-hidden="true"></span>
                    <template #popper>
                        Deze gegevens zijn optioneel, maar kunnen handig zijn voor de organisatie.<br />
                        Je kunt een noodnummer opgeven zodat we iemand kunnen bereiken in geval van nood.<br />
                        Ook kun je hier eventuele bijzonderheden of aandachtspunten doorgeven, zoals medische informatie
                        of andere zaken waar we rekening mee moeten houden.
                    </template>
                </Tooltip>
            </h2>
            <button type="button" class="btn optional p-0 d-flex align-items-center text-left"
                @click="showAdditionalFields = !showAdditionalFields">
                <span :class="showAdditionalFields ? 'icon-minus' : 'icon-plus'" class="mr-1" aria-hidden="true"></span>
                Noodnummer of belangrijke informatie meegeven
            </button>
            <div v-if="showAdditionalFields" class="mt-2">
                <!-- Noodnummer -->
                <div class="form-label-group mb-2" :class="{ 'has-error': v$.account.Noodnummer.$error }">
                    <input type="tel" class="form-label-group-input" id="noodnummer" name="noodnummer"
                        v-model="account.Noodnummer" placeholder="Telefoonnummer (niet je eigen nummer)"
                        @blur="v$.account.Noodnummer.$touch()" />
                    <label for="noodnummer">Telefoonnummer bij nood</label>
                    <div v-for="err in getFieldErrors('account.Noodnummer')" :key="err" class="ic-error" role="alert">
                        <small class="text-danger">{{ err }}</small>
                    </div>
                </div>
                <!-- Bijzonderheden -->
                <div class="form-label-group mb-0">
                    <textarea class="form-label-group-input" rows="4" id="extraInfo"
                        placeholder="Bijv: Pieter heeft last van zijn knie, kunnen jullie daar rekening mee houden?"
                        v-model="boeking.Bijzonderheden"></textarea>
                    <label for="extraInfo">Zijn er nog belangrijke dingen die wij moeten weten?</label>
                </div>
            </div>
        </div>

        <!-- Account aanmaken (gastboeker) -->
        <div v-if="isGast" class="mt-4 mb-2">
            <h2 class="payment-title h4 mb-2">Maak een account</h2>
            <div class="custom-control custom-checkbox">
                <input class="custom-control-input" type="checkbox" id="upgradeProfile" name="upgradeProfile"
                    v-model="store.upgradeProfile" />
                <label class="custom-control-label" for="upgradeProfile">
                    Maak een profiel aan om makkelijk je boeking te beheren.
                </label>
            </div>
        </div>

        <!-- Desktop stepper -->
        <hr class="payment-stepper-divider mt-4" />

        <div v-if="requiresExtraKostenAkkoord" class="mt-3 extra-kosten-box">
            <div class="custom-control custom-checkbox">
                <input class="custom-control-input" type="checkbox" name="extraKostenAkkoord" id="extraKostenAkkoord"
                    v-model="boeking.ExtraKostenAkkoord" :required="requiresExtraKostenAkkoord"
                    @change="extraKostenAkkoordTouched = true" @blur="extraKostenAkkoordTouched = true" />
                <label class="custom-control-label d-flex" for="extraKostenAkkoord">
                    Het is mij bekend dat er voor deze activiteit extra kosten zijn
                    <Tooltip :triggers="['click', 'hover']" :theme="'payment-info'">
                        <span class="icon icon-info" aria-hidden="true"></span>
                        <template #popper>
                            <template v-if="store.activiteit?.ExtraKostenBeschrijving">
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <span v-html="store.activiteit.ExtraKostenBeschrijving"></span>
                            </template>
                        </template>
                    </Tooltip>
                </label>
            </div>
            <small v-if="extraKostenAkkoordTouched && !isExtraKostenAkkoordValid" class="text-danger">
                Geef aan dat de informatie bekend is om de bestelling af te ronden
            </small>
        </div>

        <div class="mt-3 extra-kosten-box">
            <div class="custom-control custom-checkbox">
                <input class="custom-control-input" type="checkbox" name="akkoordVoorwaarden" id="akkoordVoorwaarden"
                    v-model="boeking.AkkoordVoorwaarden" @change="akkoordVoorwaardenTouched = true"
                    @blur="akkoordVoorwaardenTouched = true" />
                <label class="custom-control-label d-flex" for="akkoordVoorwaarden">
                    {{ algemeneVoorwaardenLabel }}
                </label>
            </div>
            <small v-if="akkoordVoorwaardenTouched && !isAkkoordVoorwaardenValid" class="text-danger">
                Je dient akkoord te gaan met de algemene voorwaarden om de bestelling af te ronden
            </small>

        </div>

        <div class="payment-stepper-desktop-general-terms-text">
            <template v-if="store.algemeneVoorwaardenText">
                <div v-html="store.algemeneVoorwaardenText"></div>
            </template>
        </div>


        <div class="payment-stepper-desktop d-none d-md-flex">
            <button type="button" class="btn btn-primary general-terms-button" @click="handleVolgende"
                :disabled="store.isSubmitting || !store.isGolfstartAllowed || !isExtraKostenAkkoordValid || !isAkkoordVoorwaardenValid || !store.isInschrijvingPeriodeActief">
                <span v-if="store.isSubmitting" class="spinner-border spinner-border-sm mr-1" role="status"
                    aria-hidden="true"></span>
                Bestelling afronden
            </button>
        </div>

    </div>
</template>
